import React from 'react';
import { Switch, Input, Checkbox, Button, Typography } from 'antd';
import { PlusOutlined, CloseOutlined } from '@ant-design/icons';
import { T } from 'react-translator-component';
import {
  FormInputGroupWrapper,
  FormInputWrapper,
  FormLabel,
  Section,
  SwitchWrapper,
} from './styled';
import {
  ScheduleSection,
  ScheduleDescription,
  DayScheduleWrapper,
  DayRow,
  DayLabel,
  ClosedLabel,
  PeriodRow,
  PeriodBlock,
  PeriodsWrapper,
  HintText,
  EmptyState,
} from './idleExemptStyled';
import { findOverlappingDays } from './idleExemptValidation';

const { Title } = Typography;

const DAYS = [
  { value: 1, key: 'Sunday' },
  { value: 2, key: 'Monday' },
  { value: 3, key: 'Tuesday' },
  { value: 4, key: 'Wednesday' },
  { value: 5, key: 'Thursday' },
  { value: 6, key: 'Friday' },
  { value: 7, key: 'Saturday' },
];

const path = (namespace, field) => (namespace ? `${namespace}.${field}` : field);

const get = (obj, dotted) =>
  dotted.split(/\.|\[|\]/).filter(Boolean).reduce((acc, k) => (acc == null ? acc : acc[k]), obj);

const IdleExemptSection = ({ values, setFieldValue, namespace = '', disabled = false, showTitle = true }) => {
  const alwaysCharge    = get(values, path(namespace, 'idleExemptAlwaysCharge'));
  const customizePerDay = get(values, path(namespace, 'idleExemptCustomizePerDay'));
  const schedules       = get(values, path(namespace, 'idleExemptSchedules')) || [];

  const setAlwaysCharge = (v) => {
    setFieldValue(path(namespace, 'idleExemptAlwaysCharge'), v);
    if (v) setFieldValue(path(namespace, 'idleExemptCustomizePerDay'), false);
  };
  const setCustomize = (v) => setFieldValue(path(namespace, 'idleExemptCustomizePerDay'), v);
  const setSchedules = (arr) => setFieldValue(path(namespace, 'idleExemptSchedules'), arr);

  const addSlot    = (dayValue) => setSchedules([...schedules, { dayOfWeek: dayValue, startTime: '22:00', endTime: '06:00' }]);
  const removeSlot = (globalIdx) => setSchedules(schedules.filter((_, i) => i !== globalIdx));
  const updateSlot = (globalIdx, patch) => {
    const next = [...schedules];
    next[globalIdx] = { ...next[globalIdx], ...patch };
    setSchedules(next);
  };
  const toggleDay = (day, checked) => {
    if (checked) addSlot(day.value);
    else setSchedules(schedules.filter((s) => s.dayOfWeek !== day.value));
  };

  return (
    <>
      {showTitle && (
        <Section>
          <Title level={5} style={{ marginBottom: 24 }}>
            {T('Do not charge idle time')}
          </Title>
        </Section>
      )}

      <FormInputGroupWrapper>
        <FormInputWrapper>
          <FormLabel>{T('Idle charging 24 hours')}:</FormLabel>
          <SwitchWrapper>
            <Switch
              checked={!!alwaysCharge}
              disabled={disabled}
              onChange={setAlwaysCharge}
            />
          </SwitchWrapper>
        </FormInputWrapper>
      </FormInputGroupWrapper>

      {!alwaysCharge && (
        <>
          <FormInputGroupWrapper>
            <FormInputWrapper>
              <FormLabel>{T('Customize idle exemption per day')}:</FormLabel>
              <SwitchWrapper>
                <Switch
                  checked={!!customizePerDay}
                  disabled={disabled}
                  onChange={setCustomize}
                />
              </SwitchWrapper>
            </FormInputWrapper>
          </FormInputGroupWrapper>

          {!customizePerDay && (
            <ScheduleSection>
              <ScheduleDescription>{T('Idle exemption description')}</ScheduleDescription>
              <div style={{ flex: 1 }}>
                <EmptyState>{T('Idle exemption inactive empty state')}</EmptyState>
              </div>
            </ScheduleSection>
          )}

          {customizePerDay && (
            <ScheduleSection>
              <ScheduleDescription>{T('Idle exemption description')}</ScheduleDescription>
              <DayScheduleWrapper>
                {(() => {
                  const overlappingDays = findOverlappingDays(schedules);
                  return overlappingDays.size > 0 ? (
                    <div
                      style={{
                        padding: '10px 20px',
                        background: '#FEF2F2',
                        borderBottom: '1px solid #FCA5A5',
                        color: '#B91C1C',
                        fontSize: 13,
                        fontWeight: 500,
                      }}
                    >
                      ⚠ {T('Idle exemption periods overlap')}
                    </div>
                  ) : null;
                })()}
                {DAYS.map((day) => {
                  const daySchedules = schedules
                    .map((s, i) => ({ s, i }))
                    .filter(({ s }) => s.dayOfWeek === day.value);
                  const active = daySchedules.length > 0;

                  return (
                    <DayRow key={day.value} data-testid={`ies-day-${day.value}`}>
                      <Checkbox
                        checked={active}
                        disabled={disabled}
                        onChange={(e) => toggleDay(day, e.target.checked)}
                      />
                      <DayLabel>{T(day.key)}</DayLabel>
                      {active ? (
                        <PeriodsWrapper>
                          {daySchedules.map(({ s, i }, dIdx) => (
                            <PeriodBlock key={i}>
                              <PeriodRow>
                                <Input
                                  type='time'
                                  size='large'
                                  style={{ flex: 1, minWidth: 180 }}
                                  value={s.startTime}
                                  onChange={(e) => updateSlot(i, { startTime: e.target.value })}
                                  disabled={disabled}
                                />
                                <Input
                                  type='time'
                                  size='large'
                                  style={{ flex: 1, minWidth: 180 }}
                                  value={s.endTime}
                                  onChange={(e) => updateSlot(i, { endTime: e.target.value })}
                                  disabled={disabled}
                                />
                                {daySchedules.length > 1 && (
                                  <Button
                                    type='text'
                                    size='small'
                                    aria-label={`remove-${day.value}-${dIdx}`}
                                    icon={<CloseOutlined style={{ fontSize: 12 }} />}
                                    style={{ color: '#999' }}
                                    onClick={() => removeSlot(i)}
                                    disabled={disabled}
                                  />
                                )}
                                {dIdx === daySchedules.length - 1 && daySchedules.length < 4 && (
                                  <Button
                                    type='text'
                                    size='small'
                                    aria-label={`add-${day.value}`}
                                    icon={<PlusOutlined style={{ fontSize: 14 }} />}
                                    style={{ color: '#999' }}
                                    onClick={() => addSlot(day.value)}
                                    disabled={disabled}
                                  />
                                )}
                              </PeriodRow>
                              {daySchedules.length === 1 && s.startTime && s.endTime && (
                                <HintText>
                                  {T('Idle exemption hint prefix')} {s.startTime} {T('Idle exemption hint separator')} {s.endTime} {T('Idle exemption hint suffix')}
                                </HintText>
                              )}
                            </PeriodBlock>
                          ))}
                        </PeriodsWrapper>
                      ) : (
                        <ClosedLabel>{T('Charge idle time all day')}</ClosedLabel>
                      )}
                    </DayRow>
                  );
                })}
              </DayScheduleWrapper>
            </ScheduleSection>
          )}
        </>
      )}
    </>
  );
};

export default IdleExemptSection;
